import { Pill, Row, useFieldLabelling, type Tone } from '@leanin/ui';
import { useFilterGroupLabelId } from './filter-group-label';

export type PillFilterOption = {
  value: string;
  label: string;
  count?: number;
};

type SingleProps = {
  multi?: false;
  value: string;
  onChange: (next: string) => void;
  allowDeselect?: boolean;
};

type MultiProps = {
  multi: true;
  value: ReadonlyArray<string>;
  onChange: (next: string[]) => void;
};

export type PillFilterProps = (SingleProps | MultiProps) & {
  options: ReadonlyArray<PillFilterOption>;
  selectedTone?: Tone;
  tone?: Tone;
  disabled?: boolean;
  /** Names the set where neither a Field label nor a FilterGroup heading does. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
};

// A role="group" of toggle pills. Inside a <Field> the Field label names it
// and the description / error describe it (useFieldLabelling); inside a
// FilterGroup the group heading names it. A name the caller passes wins.
export function PillFilter(props: PillFilterProps) {
  const { options, selectedTone = 'burgundy', tone = 'neutral', disabled = false } = props;
  const field = useFieldLabelling();
  const filterGroupLabelId = useFilterGroupLabelId();

  const namesItself = props['aria-label'] !== undefined || props['aria-labelledby'] !== undefined;
  const labelledBy = namesItself
    ? props['aria-labelledby']
    : (field['aria-labelledby'] ?? filterGroupLabelId);

  const isSelected = (v: string) =>
    props.multi ? props.value.includes(v) : props.value === v;

  const handleClick = (v: string) => {
    if (props.multi) {
      const next = props.value.includes(v)
        ? props.value.filter((x) => x !== v)
        : [...props.value, v];
      props.onChange(next);
      return;
    }
    if (props.value === v) {
      if (props.allowDeselect) props.onChange('');
      return;
    }
    props.onChange(v);
  };

  return (
    <Row
      role="group"
      aria-label={props['aria-label']}
      aria-labelledby={labelledBy}
      aria-describedby={field['aria-describedby']}
      aria-invalid={field['aria-invalid']}
      gap="sm"
      wrap
    >
      {options.map((opt) => {
        const selected = isSelected(opt.value);
        const label =
          typeof opt.count === 'number' ? `${opt.label} (${opt.count})` : opt.label;
        return (
          <Pill
            key={opt.value}
            size="md"
            tone={selected ? selectedTone : tone}
            selected={selected}
            disabled={disabled}
            onClick={() => handleClick(opt.value)}
          >
            {label}
          </Pill>
        );
      })}
    </Row>
  );
}
